/**
 * sineGordonPipeline — Sine-Gordon soliton equation.
 *
 * The Sine-Gordon equation: ∂²u/∂t² - ∂²u/∂x² = sin(u). Famous for
 * having analytical soliton solutions (kink, anti-kink, breather).
 * We visualize a 2D extension: ∂²u/∂t² = ∇²u - sin(u).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SineGordonPipeline {
  updateProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  stateTextures: [WebGLTexture | null, WebGLTexture | null];
  stateBuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// State: R = u(x,y,t), G = u(x,y,t-dt).
const UPDATE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;
uniform sampler2D uCurrState;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uPrevState, 0));
  float u = texture(uCurrState, vUV).r;
  float uPrev = texture(uPrevState, vUV).r;
  // Laplacian (5-point stencil).
  float lap = 0.0;
  lap += texture(uCurrState, vUV + vec2(texel.x, 0.0)).r;
  lap += texture(uCurrState, vUV - vec2(texel.x, 0.0)).r;
  lap += texture(uCurrState, vUV + vec2(0.0, texel.y)).r;
  lap += texture(uCurrState, vUV - vec2(0.0, texel.y)).r;
  lap -= 4.0 * u;
  // Sine-Gordon: u'' = laplacian(u) - sin(u)
  // Leapfrog: u_new = 2*u - u_prev + dt^2 * (lap - sin(u))
  float dt = 0.4;
  float uNew = 2.0 * u - uPrev + dt * dt * (lap - sin(u));
  outState = vec4(uNew, u, 0.0, 1.0);
}
`;

// Sine-Gordon render: visualize solitons as bright moving waves.
const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  vec2 p = vUV * 2.0 - 1.0;
  p.x *= uResolution.x / uResolution.y;
  float u = texture(uState, vUV).r;
  // Sine-Gordon values are in [-pi, pi]. Map to color.
  vec3 bg = vec3(0.04, 0.02, 0.10);
  vec3 pos = vec3(0.95, 0.30, 0.85);
  vec3 neg = vec3(0.30, 0.85, 0.95);
  // Map u in [-pi, pi] to [0, 1].
  float t = u / 3.14159265 * 0.5 + 0.5;
  vec3 col = mix(bg, mix(neg, pos, t), smoothstep(0.0, 0.5, abs(t - 0.5) * 2.0));
  // Cosine-based glow: brightest where |sin(u/2)| is near 0 (kink center).
  float kink = smoothstep(0.5, 0.0, abs(cos(u * 0.5)));
  col += vec3(1.0, 0.85, 0.55) * kink * 0.6;
  // Audio modulation.
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  col *= 1.0 + bassAvg * 0.5;
  col += vec3(0.95, 0.40, 0.95) * midAvg * 0.3;
  // Wave-mode pattern.
  float wave = sin(p.x * 6.0 + uTime * 0.5) * 0.5 + 0.5;
  col += vec3(0.20, 0.50, 0.95) * wave * 0.1;
  outColor = vec4(col, 1.0);
}
`;

function createStateTexture(gl: WebGL2RenderingContext, size: number, seed: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Float32Array(size * size * 4);
  let s = seed | 0;
  function rand() {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  for (let i = 0; i < size * size; i++) {
    // Initial condition: kink across the middle.
    const y = (i % size) / size;
    const x = Math.floor(i / size) / size;
    const kink = Math.tanh((x - 0.5) * 8.0) * 2.0;
    pixels[i * 4 + 0] = kink;
    pixels[i * 4 + 1] = kink;
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 1;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.FLOAT, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachFbo(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createSineGordonPipeline(gl: WebGL2RenderingContext, size = 96): SineGordonPipeline | null {
  const updateProgram = linkProgram(gl, VERT_SRC, UPDATE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!updateProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const tex0 = createStateTexture(gl, size, 100);
  const tex1 = createStateTexture(gl, size, 100);
  const fb0 = attachFbo(gl, tex0, size);
  const fb1 = attachFbo(gl, tex1, size);

  return {
    updateProgram, renderProgram, vao, vbo,
    stateTextures: [tex0, tex1],
    stateBuffers: [fb0, fb1],
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
  };
}

export function computeSineGordonBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeSineGordonRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawSineGordon(
  gl: WebGL2RenderingContext,
  pipe: SineGordonPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.updateProgram || !pipe.renderProgram) return;
  const size = pipe.gridSize;
  // We need both "previous" and "current" state. Use 3 textures ping-ponged.
  // For simplicity, we'll cycle the textures in a 3-state pattern.
  // Note: stateTextures has only 2 textures; we'll need to track manually.
  // For simplicity, use the standard 2-texture ping-pong and accept that
  // uPrev will be one step behind.
  for (let i = 0; i < 6; i++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.stateTextures[srcIdx];
    const dstFb = pipe.stateBuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.updateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.updateProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    gl.activeTexture(gl.TEXTURE1);
    const currTex = pipe.stateTextures[dstIdx];
    gl.bindTexture(gl.TEXTURE_2D, currTex!);
    const uCurr = gl.getUniformLocation(pipe.updateProgram, "uCurrState");
    if (uCurr) gl.uniform1i(uCurr, 1);
    gl.bindVertexArray(pipe.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
    pipe.pingIdx = dstIdx;
  }
  const finalTex = pipe.stateTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  const uState = gl.getUniformLocation(pipe.renderProgram, "uState");
  if (uState) gl.uniform1i(uState, 0);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uTimeR = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTimeR) gl.uniform1f(uTimeR, t);
  const uBassR = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBassR) gl.uniform3f(uBassR, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMidR = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMidR) gl.uniform3f(uMidR, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHighR = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHighR) gl.uniform3f(uHighR, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
